import { useProducts, type Product } from '@airship-platform/airship-proposal';
import { z } from 'zod';
import { type RxDbDataService } from '../../rxdb/utils/rxDbDataService';

type FetchLookupsFn = ReturnType<typeof useProducts>['fetchLookups'];
type FetchModifiersFn = ReturnType<typeof useProducts>['fetchModifiers'];
type LookupsResponse = Awaited<ReturnType<FetchLookupsFn>>;
type ModifiersResponse = Awaited<ReturnType<FetchModifiersFn>>;

type FetchPricebookSyncManifestFn = ReturnType<
  typeof useProducts
>['fetchPricebookSyncManifest'];

type FetchPricebookSyncFileFn = (url: string) => Promise<Product[]>;

export type PricebookDataAccess = Pick<
  RxDbDataService,
  'bulkUpsert' | 'removeStale'
>;

const PRICEBOOK_SYNC_SEGMENTS = {
  services: 'SERVICE',
  equipment: 'EQUIPMENT',
  materials: 'MATERIAL',
} as const;

export type PricebookSyncSegment = keyof typeof PRICEBOOK_SYNC_SEGMENTS;

export type PricebookSyncHashes = Partial<Record<PricebookSyncSegment, string>>;

export type PricebookSyncStage = 'not-built' | 'fetch' | 'count' | 'write';

export class PricebookSyncError extends Error {
  constructor(
    public readonly segment: PricebookSyncSegment,
    public readonly stage: PricebookSyncStage,
    public readonly detail: Record<string, string | number>,
    public override readonly cause?: Error
  ) {
    super(`Pricebook sync: ${stage} failed (${segment})`, { cause });
    this.name = 'PricebookSyncError';
  }
}

export type PricebookSyncDownload = {
  segment: PricebookSyncSegment;
  itemCount: number;
  contentHash: string | null;
  fetchMs: number;
  writeMs: number;
};

export type PricebookSyncResult = {
  hashes: PricebookSyncHashes;
  downloads: PricebookSyncDownload[];
};

const ALL_SEGMENTS = Object.keys(
  PRICEBOOK_SYNC_SEGMENTS
) as PricebookSyncSegment[];

const PricebookSyncHashesSchema = z.record(z.string(), z.string());

export const parsePricebookSyncHashes = (raw?: string): PricebookSyncHashes => {
  if (!raw) {
    return {};
  }

  try {
    return PricebookSyncHashesSchema.parse(JSON.parse(raw));
  } catch {
    return {};
  }
};

const UPSERT_CHUNK_SIZE = 400;

const chunk = <T>(items: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, i) =>
    items.slice(i * size, i * size + size)
  );

export const fetchPricebookSyncFile = async (
  url: string
): Promise<Product[]> => {
  const res = await fetch(url);

  if (!res.ok) {
    throw new Error(`Pricebook sync file request failed with ${res.status}`);
  }

  return res.json();
};

export const syncPricebook = async (
  data: PricebookDataAccess,
  fetchManifest: FetchPricebookSyncManifestFn,
  fetchSyncFile: FetchPricebookSyncFileFn,
  options: { force?: boolean; syncedHashes?: PricebookSyncHashes }
): Promise<PricebookSyncResult> => {
  const manifest = await fetchManifest();
  const hashes: PricebookSyncHashes = {};
  const downloads: PricebookSyncDownload[] = [];

  for (const segment of ALL_SEGMENTS) {
    const entry = manifest[segment];
    const syncedHash = options.syncedHashes?.[segment];

    if (
      !options.force &&
      entry.contentHash &&
      entry.contentHash === syncedHash
    ) {
      hashes[segment] = syncedHash;
      continue;
    }

    if (!entry.url || entry.itemCount === null) {
      throw new PricebookSyncError(segment, 'not-built', {
        generatedAt: entry.generatedAt ?? 'none',
      });
    }

    const fetchStart = performance.now();
    const entryUrl = entry.url;
    const items = await fetchSyncFile(entryUrl).catch((error: Error) => {
      throw new PricebookSyncError(
        segment,
        'fetch',
        { url: new URL(entryUrl).pathname },
        error
      );
    });
    const fetchMs = Math.round(performance.now() - fetchStart);

    if (items.length !== entry.itemCount) {
      throw new PricebookSyncError(segment, 'count', {
        held: items.length,
        expected: entry.itemCount,
      });
    }

    const writeStart = performance.now();
    const pricebookIds: string[] = [];

    try {
      for (const batch of chunk(items, UPSERT_CHUNK_SIZE)) {
        await data.bulkUpsert('pricebook_items', batch);
        batch.forEach((item) => pricebookIds.push(item.pricebookId));
      }

      await data.removeStale('pricebook_items', 'pricebookId', pricebookIds, {
        type: PRICEBOOK_SYNC_SEGMENTS[segment],
      });
    } catch (error) {
      throw new PricebookSyncError(
        segment,
        'write',
        { itemCount: items.length, written: pricebookIds.length },
        error instanceof Error ? error : undefined
      );
    }

    downloads.push({
      segment,
      itemCount: items.length,
      contentHash: entry.contentHash,
      fetchMs,
      writeMs: Math.round(performance.now() - writeStart),
    });

    if (entry.contentHash) {
      hashes[segment] = entry.contentHash;
    }
  }

  return { hashes, downloads };
};

const processLookups = async (
  lookups: LookupsResponse,
  data: PricebookDataAccess
): Promise<void> => {
  await Promise.all([
    data.bulkUpsert('pricebook_categories', lookups.categories),
    data.bulkUpsert('pricebook_brands', lookups.brands),
  ]);

  await Promise.all([
    data.removeStale(
      'pricebook_categories',
      'categoryId',
      lookups.categories.map((c) => c.categoryId)
    ),
    data.removeStale(
      'pricebook_brands',
      'name',
      lookups.brands.map((b) => b.name)
    ),
  ]);
};

const processModifiers = async (
  modifiers: ModifiersResponse,
  data: PricebookDataAccess
): Promise<void> => {
  await Promise.all([
    data.bulkUpsert('pricebook_discounts', modifiers.discounts),
    data.bulkUpsert('pricebook_fees', modifiers.fees),
    data.bulkUpsert('pricebook_rebates', modifiers.rebates),
  ]);

  await Promise.all([
    data.removeStale(
      'pricebook_discounts',
      'discountsAndFeesId',
      modifiers.discounts.map((d) => d.discountsAndFeesId)
    ),
    data.removeStale(
      'pricebook_fees',
      'discountsAndFeesId',
      modifiers.fees.map((f) => f.discountsAndFeesId)
    ),
    data.removeStale(
      'pricebook_rebates',
      'discountsAndFeesId',
      modifiers.rebates.map((r) => r.discountsAndFeesId)
    ),
  ]);
};

export const syncLookups = async (
  data: PricebookDataAccess,
  fetchLookups: FetchLookupsFn
): Promise<void> => {
  const lookupsRes = await fetchLookups({});
  if (!lookupsRes) {
    return;
  }

  await Promise.all([
    processLookups(lookupsRes, data),
    processModifiers(lookupsRes, data),
  ]);
};

export const syncModifiers = async (
  data: PricebookDataAccess,
  fetchModifiers: FetchModifiersFn
): Promise<void> => {
  const modifiersRes = await fetchModifiers();
  if (!modifiersRes) {
    return;
  }

  await processModifiers(modifiersRes, data);
};
